import { Link } from "react-router-dom";
import { CoreIdeaDiagram } from "../../components/CoreIdeaDiagram";
import { PageHero } from "../../components/PageHero";
import { ScrollReveal } from "../../components/ScrollReveal";
import { Seo } from "../../components/Seo";
import { useLanguage } from "../../i18n/LanguageProvider";

/**
 * The fuller story behind the home page, quoting the revised content
 * document section by section. Every section is a heading followed by body
 * copy at one size (`gw-copy`), so no block mixes type sizes.
 */
export const AboutPage = () => {
  const { t, tList, path } = useLanguage();
  const stepKeys = ["recognize", "reframe", "rebuild", "reengage"] as const;

  return (
    <>
      <Seo
        titleKey="meta.about.title"
        descriptionKey="meta.about.description"
      />

      <PageHero
        variant="about"
        eyebrow={t("about.eyebrow")}
        title={t("about.title")}
        subtitle={t("about.subtitle")}
      />

      <section className="gw-section">
        <div className="gw-container">
          <div className="gw-grid gw-grid--2 gw-about-pair">
            <ScrollReveal direction="left">
              <h2 className="gw-title">{t("about.whoHeading")}</h2>
              <div className="gw-copy">
                <p>{t("about.whoBody")}</p>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={120}>
              <h2 className="gw-title">{t("about.missionHeading")}</h2>
              <div className="gw-copy">
                <p>{t("about.missionBody")}</p>
                <p>{t("about.missionDetail")}</p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={80}>
            <figure className="gw-coreidea">
              <figcaption className="gw-coreidea__text">
                <p className="gw-eyebrow">{t("about.coreIdeaLabel")}</p>
                <blockquote className="gw-coreidea__quote">
                  {t("about.coreIdeaBody")}
                </blockquote>
              </figcaption>
              <div className="gw-coreidea__figure">
                <CoreIdeaDiagram />
              </div>
            </figure>
          </ScrollReveal>
        </div>
      </section>

      <section className="gw-section gw-section--sunken">
        <div className="gw-container">
          <ScrollReveal>
            <h2 className="gw-title gw-about-heading">
              {t("about.prepareHeading")}
            </h2>
          </ScrollReveal>

          <div
            className="gw-grid gw-grid--2 gw-about-pair"
            style={{ marginTop: "var(--gw-space-5)" }}
          >
            <ScrollReveal direction="left">
              <div className="gw-copy" style={{ marginTop: 0 }}>
                {tList("about.prepareBody").map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" delay={120}>
              <div className="gw-card gw-about-card">
                <h3 className="gw-about-card__title">
                  {t("about.lifeHeading")}
                </h3>
                <ul className="gw-pill-list">
                  {tList("about.lifeItems").map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <hr className="gw-divider" />
                <div className="gw-copy">
                  <p>{t("about.beingNote")}</p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={80}>
            <div className="gw-about-callout gw-copy">
              <p>{t("about.usableBody")}</p>
              <p className="gw-copy__emphasis">{t("about.usableClosing")}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="gw-section">
        <div className="gw-container">
          <ScrollReveal>
            <h2 className="gw-title">{t("approach.title")}</h2>
            <div className="gw-copy">
              <p>{t("approach.body")}</p>
              <p className="gw-copy__emphasis">{t("approach.chainIntro")}</p>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={120}>
            <ol className="gw-chain" aria-label={t("approach.chainLabel")}>
              {tList("approach.chain").map((stage) => (
                <li key={stage}>{stage}</li>
              ))}
            </ol>
            <div className="gw-copy" style={{ marginTop: "var(--gw-space-6)" }}>
              <p>{t("approach.note")}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section id="framework" className="gw-section gw-section--sunken">
        <div className="gw-container">
          <ScrollReveal>
            <h2 className="gw-title">{t("model.title")}</h2>
            <div className="gw-copy">
              <p>{t("model.intro")}</p>
            </div>
          </ScrollReveal>

          <div className="gw-grid gw-grid--2" style={{ marginTop: "2.5rem" }}>
            {stepKeys.map((key, index) => (
              <ScrollReveal key={key} delay={index * 80}>
                <article className="gw-card gw-about-card">
                  <p className="gw-step__number">
                    {t(`model.steps.${key}.number`)}
                  </p>
                  <h3 className="gw-about-card__title">
                    {t(`model.steps.${key}.title`)}
                  </h3>
                  <div className="gw-copy">
                    <p>{t(`model.steps.${key}.body`)}</p>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="gw-section gw-section--ink">
        <div className="gw-container gw-container--narrow">
          <ScrollReveal>
            <h2 className="gw-title">{t("about.visionHeading")}</h2>
            <div className="gw-copy">
              <p>{t("about.visionBody")}</p>
              <p>{t("about.visionDetail")}</p>
              <p>{t("about.visionGoal")}</p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="gw-section gw-section--gradient">
        <div
          className="gw-container gw-container--narrow"
          style={{ textAlign: "center" }}
        >
          <ScrollReveal>
            <h2 className="gw-title">{t("home.ctaTitle")}</h2>
            <p className="gw-subtitle" style={{ marginInline: "auto" }}>
              {t("home.ctaBody")}
            </p>
            <p style={{ marginTop: "1.75rem" }}>
              <Link to={path("/contact")} className="gw-btn gw-btn--on-dark">
                {t("home.ctaButton")}
              </Link>
            </p>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
};
